import * as React from 'react';

import { useTheme } from '~/shared/hooks';

type UseInventoryGroupStyleProps = {
  colorHue?: number | null;
};

export type InventoryGroupStyle = {
  hue: number | null;
  backgroundColor: string;
  borderColor: string;
  color: string;
  '--hover-bg': string;
  '--hover-color': string;
};

const createGroupStyle = (
  hue: number | null,
  effectiveColorScheme: 'light' | 'dark',
): InventoryGroupStyle => {
  const isLight = effectiveColorScheme === 'light';

  if (hue == null || hue === 0) {
    return {
      hue,
      backgroundColor: isLight
        ? 'rgba(255, 255, 255, 0.9)'
        : 'rgba(255, 255, 255, 0.08)',
      borderColor: isLight
        ? 'rgba(var(--ink-neutral), .2)'
        : 'rgba(255, 255, 255, 0.15)',
      color: 'rgba(var(--ink-neutral), .8)',
      '--hover-bg': isLight
        ? 'rgba(var(--ink-neutral), 0.08)'
        : 'rgba(255, 255, 255, 0.12)',
      '--hover-color': 'rgba(var(--ink-neutral), .9)',
    };
  }

  return {
    hue,
    backgroundColor: `hsla(${hue}, 65%, 50%, 0.25)`,
    borderColor: `hsl(${hue}, 55%, 70%)`,
    color: isLight ? `hsl(${hue}, 80%, 27%)` : `hsl(${hue}, 55%, 70%)`,
    '--hover-bg': `hsla(${hue}, 65%, 50%, 0.35)`,
    '--hover-color': isLight
      ? `hsl(${hue}, 80%, 20%)`
      : `hsl(${hue}, 55%, 77%)`,
  };
};

export const useInventoryGroupStyle = ({
  colorHue,
}: UseInventoryGroupStyleProps): InventoryGroupStyle => {
  const { effectiveColorScheme } = useTheme();

  return React.useMemo(
    () => createGroupStyle(colorHue ?? null, effectiveColorScheme),
    [colorHue, effectiveColorScheme],
  );
};
